// src/Components/UserActivities/ChartUserDistribution.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { PieChart } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Stack,
    LinearProgress,
    linearProgressClasses,
} from '@mui/material';

/* ---------- styled helpers ---------- */
const StyledText = styled('text')(({ theme, variant }) => ({
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fill: theme.palette.text.secondary,
    fontSize:
        variant === 'primary'
            ? theme.typography.h5.fontSize
            : theme.typography.body2.fontSize,
    fontWeight:
        variant === 'primary'
            ? theme.typography.h5.fontWeight
            : theme.typography.body2.fontWeight,
}));

function PieCenterLabel({ primaryText, secondaryText }) {
    const { width, height, left, top } = useDrawingArea();
    const primaryY = top + height / 2 - 10;
    const secondaryY = primaryY + 24;
    return (
        <>
            <StyledText variant="primary" x={left + width / 2} y={primaryY}>
                {primaryText}
            </StyledText>
            <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
                {secondaryText}
            </StyledText>
        </>
    );
}

/* ---------- main component ---------- */
export default function ChartUserDistribution({ userTotals, users, t }) {
    /* ------------------------------------------------------------------
     * ‼️ userTotals can arrive in two shapes:
     *   1.  [{ id:'208219212', total: 450 }, ...]                 (array)
     *   2.  { '2025-05-01': [ { userId:'…', totalExcludingOther: …}, …], … }
     *       (object keyed by day → array per-user)
     * ------------------------------------------------------------------ */

    /* step-1  ── normalise to one array of { id, total } */
    const totalsArr = React.useMemo(() => {
        // already the right shape
        if (Array.isArray(userTotals)) {
            return userTotals;
        }

        // object ⇒ aggregate across all days
        const map = {};
        Object.values(userTotals || {}).forEach((dayArr) => {
            (dayArr || []).forEach((rec) => {
                const id = rec.userId;
                const inc = rec.totalExcludingOther ?? 0;
                map[id] = (map[id] || 0) + inc;
            });
        });

        return Object.entries(map).map(([id, total]) => ({ id, total }));
    }, [userTotals]);

    /* bail out if nothing to show */
    if (!totalsArr.length) {
        return (
            <Card variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {t('No data for selected range')}
                </Typography>
            </Card>
        );
    }

    /* step-2  ── convert to chart-friendly data */
    const data = totalsArr.map((u) => {
        const match = users.find((x) => x.value === u.id);
        return { label: match ? match.label : u.id, value: u.total };
    });
    const totalSum = totalsArr.reduce((s, u) => s + u.total, 0);

    /* basic colour palette – repeat if >4 users */
    const colors = [
        'hsl(220,20%,65%)',
        'hsl(220,20%,42%)',
        'hsl(220,20%,35%)',
        'hsl(220,20%,25%)',
    ];

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={{ pb: '8px !important' }}>
                <Typography component="h2" variant="subtitle2" gutterBottom>
                    {t('Users by Activity')}
                </Typography>

                {/* pie chart */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <PieChart
                        skipAnimation
                        colors={colors}
                        margin={{ left: 60, right: 60, top: 60, bottom: 60 }}
                        series={[
                            {
                                data,
                                innerRadius: 60,
                                outerRadius: 80,
                                arcLabel: () => '',
                                highlightScope: { fade: 'global', highlight: 'item' },
                            },
                        ]}
                        slotProps={{
                            legend: {
                                hidden: true
                            },
                        }}
                        height={200}
                        width={500}
                        hideLegend
                    >
                        <PieCenterLabel
                            primaryText={totalSum.toLocaleString()}
                            secondaryText={t("Total")}
                        />
                    </PieChart>
                </Box>

                {/* legend / bars */}
                {data.map((entry, idx) => {
                    const pct = totalSum ? (entry.value / totalSum) * 100 : 0;
                    return (
                        <Stack
                            key={entry.label}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ pb: 1 }}
                        >
                            <Box
                                sx={{
                                    width: 16,
                                    height: 16,
                                    backgroundColor: colors[idx % colors.length],
                                    borderRadius: 0.5,
                                }}
                            />
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {entry.label}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', ml: 'auto' }}
                            >
                                {Math.round(pct)}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={pct}
                                sx={{
                                    flexGrow: 1,
                                    ml: 1,
                                    [`& .${linearProgressClasses.bar}`]: {
                                        backgroundColor: colors[idx % colors.length],
                                    },
                                }}
                            />
                        </Stack>
                    );
                })}
            </CardContent>
        </Card>
    );
}

/* ---------- prop-types ---------- */
ChartUserDistribution.propTypes = {
    /* may be an array or the big object – accept anything */
    userTotals: PropTypes.any.isRequired,
    users: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
        })
    ).isRequired,
};
